import { withI18n } from '@lingui/react'
import classNames from 'classnames'
import { bool, oneOf, shape, string } from 'prop-types'
import { compose } from 'redux'

import { OFFER_TYPE_MIRAKL, OFFER_TYPE_SAE } from 'shared/consts/marketplace'
import {
  AVAILABLE_PRESCRIPTION_TYPES,
  AVAILABLE_PRODUCT_TEMPLATES,
  PRODUCT_TEMPLATES,
} from 'shared/consts/productTemplates'
import { TENANT_COM, TENANT_IT } from 'shared/consts/tenants'
import ButtonAsLink from 'views/components/molecules/ButtonAsLink'
import BonusColumn from 'views/components/organisms/ProductVariants/BonusColumn'
import {
  baseClasses,
  getListPriceColumn,
  getMiraklListPriceColumn,
  getYourPriceColumn,
  shouldUseOTCMode,
} from 'views/components/organisms/ProductVariants/helpers'
import { PRESCRIPTION_BONUS_MODAL_ID } from 'views/consts'
import withShopAndRuntimeConfig from 'views/enhancers/withShopAndRuntimeConfig'
import { i18nType } from 'views/utils/propTypes'

import ProductVariantHeaderColumn from './ProductVariantHeaderColumn'

import './ProductVariantsHeader.scss'

const ProductVariantsHeader = ({
  productTemplate,
  prescriptionType,
  pharmaObl,
  offerType,
  publicRuntimeConfig: {
    publicConfig: { tenant, superScripts },
  },
  i18n,
  className,
}) => {
  const useOTCMode = shouldUseOTCMode({ prescriptionType, productTemplate })
  const yourPriceColumn = getYourPriceColumn({
    prescriptionType,
    useOTCMode,
    superScripts,
    pharmaObl,
    offerType,
    i18n,
  })
  const listPriceColumn = getListPriceColumn({
    superScripts,
    pharmaObl,
  })
  const miraklListPriceColumn = getMiraklListPriceColumn({
    superScripts,
    pharmaObl,
  })
  const headerClassName = classNames(
    className,
    'm-ProductVariants__header l-flex l-flex--stretch-children a-list-reset u-padding',
    {
      'm-ProductVariants__header--prescription':
        productTemplate === PRODUCT_TEMPLATES.RX ||
        productTemplate === PRODUCT_TEMPLATES.OTX,
      'm-ProductVariants__header--bonus': !useOTCMode,
    }
  )
  const avpUvpClassNames = classNames(
    baseClasses,
    'u-color--grey m-ProductVariants__price-column u-hidden@pre-medium-small-tiny l-col--2-of-12 u-no-wrap',
    { 'u-text-align--right': tenant !== TENANT_COM }
  )
  const youPriceClassNames = classNames(
    baseClasses,
    'u-text-align--right m-ProductVariants__price-column l-col--3-of-12@medium u-no-wrap',
    { 'u-margin-large--left@medium': tenant === TENANT_IT },
    { 'u-color--red': useOTCMode }
  )

  const bonusColumnClassNames = classNames(
    baseClasses,
    'm-ProductVariants__info-column u-text-align--right u-no-wrap l-col--3-of-12@pre-medium l-col--4-of-12',
    { 'u-color--rxgreen': productTemplate === PRODUCT_TEMPLATES.RX }
  )
  const showBonusModal =
    productTemplate !== PRODUCT_TEMPLATES.OTC && !useOTCMode

  const bonusColumnRendered = (
    <BonusColumn useOTCMode={useOTCMode} superScripts={superScripts} />
  )

  return (
    <ul className={headerClassName}>
      {/* this one is rendered empty so it pushes the next column to correct position */}
      <ProductVariantHeaderColumn className="l-flex__primary m-ProductVariants__first-column" />

      {/* this one is first visible column (aka list price / avpUvp column) */}
      <ProductVariantHeaderColumn
        className={avpUvpClassNames}
        id="variants_title_avp"
      >
        {offerType === OFFER_TYPE_MIRAKL
          ? miraklListPriceColumn
          : listPriceColumn}
      </ProductVariantHeaderColumn>

      {/* this one is second column (aka retail price) */}
      <ProductVariantHeaderColumn
        className={youPriceClassNames}
        id="variants_title_price"
      >
        {yourPriceColumn}
      </ProductVariantHeaderColumn>

      {/* this one is third column (aka bonus/discount) */}
      <ProductVariantHeaderColumn
        className={bonusColumnClassNames}
        id="variants_title_saving"
      >
        <ButtonAsLink
          name="prescription_info_1"
          data-a11y-dialog-show={PRESCRIPTION_BONUS_MODAL_ID}
          isClientsideControlled
          inheritColor
          underlined
          ariaHidden={showBonusModal ? null : 'true'}
          className="o-ProductVariantsBody--bonus-label"
        >
          {bonusColumnRendered}
        </ButtonAsLink>
        <span
          aria-hidden={showBonusModal ? 'true' : null}
          className="o-ProductVariantsBody--bonus-label"
        >
          {bonusColumnRendered}
        </span>
      </ProductVariantHeaderColumn>
    </ul>
  )
}

ProductVariantsHeader.propTypes = {
  productTemplate: oneOf(AVAILABLE_PRODUCT_TEMPLATES).isRequired,
  prescriptionType: oneOf(AVAILABLE_PRESCRIPTION_TYPES.ALL).isRequired,
  pharmaObl: bool,
  publicRuntimeConfig: shape({}),
  i18n: i18nType.isRequired,
  offerType: string,
  className: string,
}

ProductVariantsHeader.defaultProps = {
  pharmaObl: false,
  publicRuntimeConfig: {},
  offerType: OFFER_TYPE_SAE,
  className: null,
}

export default compose(
  withI18n(),
  withShopAndRuntimeConfig
)(ProductVariantsHeader)
